/**
 * ============================================================================
 * [P6-01] useReferences Hook
 *
 * 레퍼런스(저장된 글, 트래킹 포스트) 로드 및 관리
 *
 * 기능:
 * - 저장된 글 목록 로드 (texts 컬렉션)
 * - 트래킹 포스트 목록 로드 (posts 컬렉션)
 * - 최근 사용 레퍼런스 관리 (localStorage)
 *
 * [2026-01-31] Phase 6 구현
 * ============================================================================
 */

import { useState, useCallback, useEffect } from 'react'
import { useAuth } from './useAuth'
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
} from 'firebase/firestore'
import { db } from '@/services/firebase'
import type { ReferenceItem } from '@/types'

// ============================================================================
// Constants
// ============================================================================
const RECENT_REFS_KEY = 'recent_references'
const MAX_RECENT = 5

function calculatePerformanceScore(metrics: {
  views?: number
  likes?: number
  comments?: number
}): number {
  const views = metrics.views ?? 0
  const likes = metrics.likes ?? 0
  const comments = metrics.comments ?? 0

  return Math.round(views * 0.1 + likes * 3 + comments * 5)
}

function calculateEngagementRate(metrics: {
  views?: number
  likes?: number
  comments?: number
}): number | undefined {
  const views = metrics.views ?? 0
  if (views <= 0) return undefined

  const likes = metrics.likes ?? 0
  const comments = metrics.comments ?? 0
  return Number((((likes + comments) / views) * 100).toFixed(1))
}

// ============================================================================
// Return Type
// ============================================================================
export interface UseReferencesReturn {
  isLoading: boolean
  error: Error | null

  // 데이터 로드
  loadSavedReferences: (
    search?: string,
    category?: string
  ) => Promise<ReferenceItem[]>
  loadTrackingReferences: (search?: string) => Promise<ReferenceItem[]>

  // 최근 사용
  recentReferences: ReferenceItem[]
  addToRecent: (item: ReferenceItem) => void
  clearRecent: () => void

  // 카테고리
  savedCategories: string[]
}

// ============================================================================
// useReferences Hook
// ============================================================================
export function useReferences(): UseReferencesReturn {
  // ==========================================================================
  // Hooks
  // ==========================================================================
  const { user } = useAuth()

  // ==========================================================================
  // State
  // ==========================================================================
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [recentReferences, setRecentReferences] = useState<ReferenceItem[]>([])
  const [savedCategories, setSavedCategories] = useState<string[]>([])

  // ==========================================================================
  // 최근 사용 레퍼런스 로드 (localStorage)
  // ==========================================================================
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_REFS_KEY)
      if (stored) {
        setRecentReferences(JSON.parse(stored))
      }
    } catch (err) {
      console.warn('[useReferences] localStorage 로드 실패:', err)
    }
  }, [])

  // ==========================================================================
  // 저장된 글 로드 (texts 컬렉션)
  // ==========================================================================
  const loadSavedReferences = useCallback(
    async (search?: string, category?: string): Promise<ReferenceItem[]> => {
      if (!user) return []

      setIsLoading(true)
      setError(null)

      try {
        const textsRef = collection(db, 'users', user.uid, 'texts')

        // 검색어가 있으면 limit 제거 (전체에서 검색)
        // 검색어가 없으면 최신 500개까지 표시
        const q = search?.trim()
          ? query(textsRef, orderBy('createdAt', 'desc'))
          : query(textsRef, orderBy('createdAt', 'desc'), limit(500))

        const snapshot = await getDocs(q)
        const items: ReferenceItem[] = []
        const categories = new Set<string>()

        snapshot.forEach((doc) => {
          const data = doc.data()

          // 삭제된 항목 제외 (클라이언트 사이드 필터링)
          if (data.isDeleted) return

          // [A안] script 타입 제외 — 레퍼런스 로더에서도 일관성 유지
          if (data.type === 'script') return

          // 카테고리 수집
          if (data.topic) categories.add(data.topic)

          // 검색 필터 (content + topic + title)
          if (search?.trim()) {
            const searchLower = search.trim().toLowerCase()
            const matchTitle = data.title?.toLowerCase().includes(searchLower)
            const matchContent = data.content
              ?.toLowerCase()
              .includes(searchLower)
            const matchTopic = data.topic?.toLowerCase().includes(searchLower)
            if (!matchTitle && !matchContent && !matchTopic) return
          }

          // 카테고리 필터
          if (category && data.topic !== category) return

          items.push({
            id: doc.id,
            title: data.title || '제목 없음',
            content: data.content || '',
            sourceType: 'saved',
            category: data.topic,
            createdAt: data.createdAt?.toDate?.() ?? new Date(),
          })
        })

        setSavedCategories(Array.from(categories).sort())
        return items
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        console.error('[useReferences.loadSavedReferences] Error:', error)
        return []
      } finally {
        setIsLoading(false)
      }
    },
    [user]
  )

  // ==========================================================================
  // 트래킹 포스트 로드 (posts 컬렉션)
  // ==========================================================================
  const loadTrackingReferences = useCallback(
    async (search?: string): Promise<ReferenceItem[]> => {
      if (!user) return []

      setIsLoading(true)
      setError(null)

      try {
        const postsRef = collection(db, 'users', user.uid, 'posts')
        const q = query(postsRef, orderBy('updatedAt', 'desc'), limit(50))

        const snapshot = await getDocs(q)
        const items: ReferenceItem[] = []

        snapshot.forEach((doc) => {
          const data = doc.data()

          // 검색 필터
          if (search) {
            const searchLower = search.toLowerCase()
            const matchTitle = data.title?.toLowerCase().includes(searchLower)
            const matchContent = data.content
              ?.toLowerCase()
              .includes(searchLower)
            if (!matchTitle && !matchContent) return
          }

          // 최신 메트릭 가져오기
          const latestMetric =
            data.metrics?.length > 0
              ? data.metrics[data.metrics.length - 1]
              : null

          const referenceMetrics = latestMetric
            ? {
                views: latestMetric.views ?? 0,
                likes: latestMetric.likes ?? 0,
                comments: latestMetric.comments ?? 0,
              }
            : undefined

          items.push({
            id: doc.id,
            title: data.title || '제목 없음',
            content: data.content || '',
            sourceType: 'tracking',
            platform: data.platform,
            createdAt: data.createdAt?.toDate?.() ?? new Date(),
            metrics: referenceMetrics,
            performanceScore: referenceMetrics
              ? calculatePerformanceScore(referenceMetrics)
              : undefined,
            engagementRate: referenceMetrics
              ? calculateEngagementRate(referenceMetrics)
              : undefined,
          })
        })

        return items
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        console.error('[useReferences.loadTrackingReferences] Error:', error)
        return []
      } finally {
        setIsLoading(false)
      }
    },
    [user]
  )

  // ==========================================================================
  // 최근 사용에 추가
  // ==========================================================================
  const addToRecent = useCallback((item: ReferenceItem) => {
    setRecentReferences((prev) => {
      // 중복 제거 후 맨 앞에 추가
      const filtered = prev.filter((r) => r.id !== item.id)
      const updated = [item, ...filtered].slice(0, MAX_RECENT)

      // localStorage 저장
      try {
        localStorage.setItem(RECENT_REFS_KEY, JSON.stringify(updated))
      } catch (err) {
        console.warn('[useReferences] localStorage 저장 실패:', err)
      }

      return updated
    })
  }, [])

  // ==========================================================================
  // 최근 사용 초기화
  // ==========================================================================
  const clearRecent = useCallback(() => {
    setRecentReferences([])
    try {
      localStorage.removeItem(RECENT_REFS_KEY)
    } catch (err) {
      console.warn('[useReferences] localStorage 삭제 실패:', err)
    }
  }, [])

  // ==========================================================================
  // Return
  // ==========================================================================
  return {
    isLoading,
    error,
    loadSavedReferences,
    loadTrackingReferences,
    recentReferences,
    addToRecent,
    clearRecent,
    savedCategories,
  }
}

export default useReferences
